import Router, { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useTrackAgentPanelOpened,
  useTrackAgentThreadOpened,
  type WorkspaceAgentThreadReopenedStatus,
} from '@air/analytics';

import { getWorkspaceAgentChatIdFromLocation } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentChatIdFromLocation';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { QueryParamNames } from '~/constants/search';
import { useZustandPersistHasHydrated } from '~/hooks/useZustandPersistHasHydrated';
import { addBeforePopStateHandler } from '~/utils/addBeforePopStateHandler';
import { setQueryParams } from '~/utils/PathUtils';
import { subscribePushedRouterHref } from '~/utils/subscribePushedRouterHref';

type PendingUrlThreadReopenedSessionIdRef = {
  current: string | null;
};

// Must match WORKSPACE_AGENT_ERRORS.LOAD_CHAT / UNAVAILABLE_CHAT in usePrivateWorkspaceAgentSession.
const WORKSPACE_AGENT_SESSION_LOAD_ERRORS = new Set(['Could not load this chat.', 'This chat is unavailable.']);

const clearPendingUrlThreadReopenedResolution = ({
  cancelled,
  pendingUrlThreadReopenedSessionIdRef,
  setPendingUrlThreadReopenedSessionId,
  targetSessionId,
}: {
  cancelled: boolean;
  pendingUrlThreadReopenedSessionIdRef: PendingUrlThreadReopenedSessionIdRef;
  setPendingUrlThreadReopenedSessionId: (sessionId: string | null) => void;
  targetSessionId: string;
}): boolean => {
  if (cancelled || pendingUrlThreadReopenedSessionIdRef.current !== targetSessionId) {
    return false;
  }

  pendingUrlThreadReopenedSessionIdRef.current = null;
  setPendingUrlThreadReopenedSessionId(null);

  return true;
};

export type UsePrivateWorkspaceAgentChatIdUrlSyncParams = {
  getActiveSessionId: () => string | null;
  getThreadReopenedStatus: (sessionId: string) => WorkspaceAgentThreadReopenedStatus | null;
  isLoadingSessions: boolean;
  resolveThreadReopenedStatus: (sessionId: string) => Promise<WorkspaceAgentThreadReopenedStatus>;
  resetSession: () => void;
  selectSession: (sessionId: string) => void;
  sessionError: string | null;
  sessionId: string | null;
};

/**
 * Keeps the open workspace-agent chat in the `chatId` query param so refresh, shared URLs, and
 * browser back/forward restore the same session. Mount once inside the panel provider.
 *
 * Session→URL waits until render `sessionId` matches `getActiveSessionId()`. `selectSession` updates
 * the ref in the same flush as URL→session; writing the still-stale render id would push an extra
 * history entry (first Back after a shared URL would close the panel).
 */
export const usePrivateWorkspaceAgentChatIdUrlSync = ({
  getActiveSessionId,
  getThreadReopenedStatus,
  isLoadingSessions,
  resolveThreadReopenedStatus,
  resetSession,
  selectSession,
  sessionError,
  sessionId,
}: UsePrivateWorkspaceAgentChatIdUrlSyncParams) => {
  const router = useRouter();
  const isRouterReady = router.isReady;
  const open = useWorkspaceAgentPanelStore((state) => state.open);
  const minimize = useWorkspaceAgentPanelStore((state) => state.minimize);
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const { trackAgentPanelOpened } = useTrackAgentPanelOpened();
  const { trackAgentThreadOpened } = useTrackAgentThreadOpened();

  const { hasHydrated: hasPanelStoreHydrated } = useZustandPersistHasHydrated(useWorkspaceAgentPanelStore.persist);
  const [locationSearch, setLocationSearch] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.search,
  );

  const prevUrlChatIdRef = useRef<string | null>(null);
  const prevSessionIdRef = useRef<string | null>(sessionId);
  const prevDockRef = useRef(dock);
  const isPopStateRef = useRef<boolean>();
  const hasAppliedInitialUrlRef = useRef<boolean>();
  const [pendingUrlThreadReopenedSessionId, setPendingUrlThreadReopenedSessionId] = useState<string | null>(null);
  const pendingUrlThreadReopenedSessionIdRef = useRef<string | null>(null);

  const queueUrlThreadReopenedResolution = useCallback((targetSessionId: string | null) => {
    pendingUrlThreadReopenedSessionIdRef.current = targetSessionId;
    setPendingUrlThreadReopenedSessionId(targetSessionId);
  }, []);

  const clearUrlThreadReopenedQueue = useCallback(() => {
    queueUrlThreadReopenedResolution(null);
  }, [queueUrlThreadReopenedResolution]);

  const trackThreadReopenedFromUrl = useCallback(
    (targetSessionId: string) => {
      const status = getThreadReopenedStatus(targetSessionId);

      if (status !== null) {
        queueUrlThreadReopenedResolution(null);
        trackAgentThreadOpened({
          is_first_run_walkthrough: false,
          session_id: targetSessionId,
          status,
          type: 'existing',
        });
        return;
      }

      queueUrlThreadReopenedResolution(targetSessionId);
    },
    [getThreadReopenedStatus, queueUrlThreadReopenedResolution, trackAgentThreadOpened],
  );

  useEffect(() => {
    const syncSearchFromHref = (href: string) => {
      setLocationSearch(new URL(href, window.location.origin).search);
    };

    const syncSearchFromWindow = () => {
      syncSearchFromHref(window.location.href);
    };

    // This effect is the client-side source for `locationSearch`. The `useState` initializer already
    // reads `window.location.search` on hydrate; seed again here so later navigations stay in sync.
    syncSearchFromWindow();

    const unsubscribePushedHref = subscribePushedRouterHref(syncSearchFromHref);
    Router.events.on('routeChangeComplete', syncSearchFromHref);

    const handlePopState = () => {
      syncSearchFromWindow();
      // Shallow query pops skip `routeChangeComplete`, so the rAF clearer in that handler never
      // runs. `setTimeout(0)` is after this flush's effects, which still see `isPopStateRef`.
      window.setTimeout(() => {
        isPopStateRef.current = false;
      }, 0);
    };

    // Shallow `chatId` pushes do not emit `routeChangeComplete` on back/forward; without this,
    // URL→session keeps the previous search while `window.location` already changed.
    window.addEventListener('popstate', handlePopState);

    return () => {
      unsubscribePushedHref();
      Router.events.off('routeChangeComplete', syncSearchFromHref);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const removeBeforePopStateHandler = addBeforePopStateHandler(
      Router,
      () => {
        isPopStateRef.current = true;
        return true;
      },
      {
        onRefused: () => {
          isPopStateRef.current = false;
        },
      },
    );

    const handleRouteChangeComplete = () => {
      requestAnimationFrame(() => {
        isPopStateRef.current = false;
      });
    };

    Router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      removeBeforePopStateHandler();
      Router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, []);

  // URL → session/panel. `chatId` present always opens that chat. `chatId` removed by the browser
  // (back/forward) closes the panel; removals we wrote ourselves (new thread, close, invalid chat)
  // already updated session/dock and are skipped via prevUrlChatIdRef.
  useEffect(() => {
    if (!isRouterReady || !hasPanelStoreHydrated) {
      return;
    }

    const urlChatId = getWorkspaceAgentChatIdFromLocation({ search: locationSearch });
    const didUrlChatIdChange = prevUrlChatIdRef.current !== urlChatId;

    if (!didUrlChatIdChange && hasAppliedInitialUrlRef.current) {
      return;
    }

    if (urlChatId) {
      const wasMinimized = dock === 'minimized';
      const isSelectingDifferentSession = urlChatId !== getActiveSessionId();

      if (isSelectingDifferentSession) {
        useWorkspaceAgentPanelStore.getState().clearPendingThreadCreatedLocation();
        selectSession(urlChatId);
        trackThreadReopenedFromUrl(urlChatId);
      }

      prevSessionIdRef.current = urlChatId;
      open();

      if (wasMinimized) {
        trackAgentPanelOpened({ location: 'url' });
      }
    } else if (isPopStateRef.current) {
      useWorkspaceAgentPanelStore.getState().clearPendingThreadCreatedLocation();
      resetSession();
      prevSessionIdRef.current = null;
      minimize();
    }

    prevUrlChatIdRef.current = urlChatId;
    hasAppliedInitialUrlRef.current = true;
  }, [
    dock,
    getActiveSessionId,
    hasPanelStoreHydrated,
    isRouterReady,
    locationSearch,
    minimize,
    open,
    resetSession,
    selectSession,
    trackThreadReopenedFromUrl,
    trackAgentPanelOpened,
  ]);

  // A cold URL can queue reopen attribution before the sessions list settles. Drop the queue when the
  // user has already left that session — minimize, new thread, a different task row, or a failed load
  // (`applySessionId(null)`). Only session-load banner errors count; send failures must not clear.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sessionId is the render commit signal after selectSession updates the ref.
  useEffect(() => {
    if (pendingUrlThreadReopenedSessionId === null) {
      return;
    }

    const hasSessionLoadError = sessionError !== null && WORKSPACE_AGENT_SESSION_LOAD_ERRORS.has(sessionError);

    if (hasSessionLoadError || getActiveSessionId() !== pendingUrlThreadReopenedSessionId) {
      clearUrlThreadReopenedQueue();
    }
  }, [clearUrlThreadReopenedQueue, getActiveSessionId, pendingUrlThreadReopenedSessionId, sessionError, sessionId]);

  // Cold shared URLs often land before the sessions list has the row; once loading settles,
  // finish any pending URL reopen that was queued while the list was still in flight.
  useEffect(() => {
    if (pendingUrlThreadReopenedSessionId === null || isLoadingSessions) {
      return;
    }

    const targetSessionId = pendingUrlThreadReopenedSessionId;
    let cancelled = false;

    void resolveThreadReopenedStatus(targetSessionId)
      .then((status) => {
        // Stale async result — the user may already be on another session with its own queued reopen.
        if (getActiveSessionId() !== targetSessionId) {
          return;
        }

        if (
          !clearPendingUrlThreadReopenedResolution({
            cancelled,
            pendingUrlThreadReopenedSessionIdRef,
            setPendingUrlThreadReopenedSessionId,
            targetSessionId,
          })
        ) {
          return;
        }

        trackAgentThreadOpened({
          is_first_run_walkthrough: false,
          session_id: targetSessionId,
          status,
          type: 'existing',
        });
      })
      .catch(() => {
        // Best-effort analytics — a failed Sessions.get must not surface as a URL sync error.
        clearPendingUrlThreadReopenedResolution({
          cancelled,
          pendingUrlThreadReopenedSessionIdRef,
          setPendingUrlThreadReopenedSessionId,
          targetSessionId,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    getActiveSessionId,
    isLoadingSessions,
    pendingUrlThreadReopenedSessionId,
    resolveThreadReopenedStatus,
    trackAgentThreadOpened,
  ]);

  // Session → URL. `sessionId` is the commit signal (open task, start, reset). Skip this flush when
  // it still lags `getActiveSessionId()` — that is URL→session having already selected in this
  // paint, and writing the stale render id would push a blank `chatId`. Skip while popstate is in
  // flight; the URL is already the destination.
  useEffect(() => {
    if (!isRouterReady || !hasPanelStoreHydrated || !hasAppliedInitialUrlRef.current) {
      return;
    }

    if (isPopStateRef.current) {
      return;
    }

    const committedSessionId = getActiveSessionId();

    if (sessionId !== committedSessionId) {
      return;
    }

    if (sessionId === prevSessionIdRef.current) {
      return;
    }

    prevSessionIdRef.current = sessionId;

    const urlChatId = getWorkspaceAgentChatIdFromLocation(window.location);

    if (sessionId === urlChatId) {
      return;
    }

    // Load failures drop an unusable `chatId` with replace so Back does not reopen it and push
    // another empty entry. New thread and other clears still push so history can restore the chat.
    setQueryParams({ [QueryParamNames.chatId]: sessionId }, sessionId === null && sessionError ? 'replace' : 'push');
    prevUrlChatIdRef.current = sessionId;
  }, [getActiveSessionId, hasPanelStoreHydrated, isRouterReady, sessionError, sessionId]);

  // Close → URL. `prevDockRef` is updated before the hydration / first-URL guards so the default
  // `minimized` dock is recorded and is not treated as the user closing after a deep link opens.
  useEffect(() => {
    const wasMinimized = prevDockRef.current === 'minimized';
    prevDockRef.current = dock;

    if (!isRouterReady || !hasPanelStoreHydrated || !hasAppliedInitialUrlRef.current) {
      return;
    }

    if (dock !== 'minimized' || wasMinimized) {
      return;
    }

    useWorkspaceAgentPanelStore.getState().clearPendingThreadCreatedLocation();
    clearUrlThreadReopenedQueue();

    if (getActiveSessionId() !== null) {
      resetSession();
      prevSessionIdRef.current = null;
    }

    const urlChatId = getWorkspaceAgentChatIdFromLocation(window.location);

    if (!urlChatId) {
      return;
    }

    setQueryParams({ [QueryParamNames.chatId]: null }, 'push');
    prevUrlChatIdRef.current = null;
  }, [clearUrlThreadReopenedQueue, dock, getActiveSessionId, hasPanelStoreHydrated, isRouterReady, resetSession]);

  // In-app (pushstate) navigations often drop query params the destination link did not spread.
  // Restore `chatId` while a chat is still selected and the panel is expanded — not on popstate,
  // where a missing `chatId` is the user going back to a URL that closed the chat.
  // `locationSearch` is the shallow-query source; `asPath` is the path-change source (board → /all
  // can share an empty search, so search alone would not re-run).
  useEffect(() => {
    if (!isRouterReady || !hasPanelStoreHydrated || !hasAppliedInitialUrlRef.current) {
      return;
    }

    const committedSessionId = getActiveSessionId();

    if (isPopStateRef.current || !committedSessionId || dock === 'minimized') {
      return;
    }

    const urlChatId =
      getWorkspaceAgentChatIdFromLocation({ search: locationSearch }) ??
      getWorkspaceAgentChatIdFromLocation(new URL(router.asPath, window.location.origin));

    if (urlChatId === committedSessionId) {
      return;
    }

    setQueryParams({ [QueryParamNames.chatId]: committedSessionId }, 'replace');
    prevUrlChatIdRef.current = committedSessionId;
  }, [dock, getActiveSessionId, hasPanelStoreHydrated, isRouterReady, locationSearch, router.asPath]);
};
